import { create } from "zustand";

interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  tenantId: string;
  permissions: string[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  fetchMe: () => Promise<void>;
}

/** Read CSRF token from cookie for inclusion in mutating requests. */
function getCsrfToken(): string {
  const match = document.cookie.match(/ztd_csrf_token=([^;]+)/);
  return match ? match[1] : "";
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,

  login: async (email, password) => {
    set({ isLoading: true });
    const res = await fetch("/api/v2/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      set({ isLoading: false });
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail ?? "Login failed");
    }
    // /api/v2/auth/login returns the raw UserOut shape — no envelope.
    const data = await res.json();
    set({
      user: {
        userId: data.id,
        email: data.email,
        displayName: data.full_name,
        role: data.role,
        // tenant_id isn't part of UserOut; the JWT carries it. The few
        // call sites that need it can read it from the access cookie or
        // fetch from /api/v2/auth/me.
        tenantId: "",
        permissions: data.permissions ?? [],
      },
      isAuthenticated: true,
      isLoading: false,
    });
  },

  logout: async () => {
    try {
      await fetch("/api/v2/auth/logout", {
        method: "POST",
        headers: { "X-CSRF-Token": getCsrfToken() },
        credentials: "include",
      });
    } catch (err) {
      // Network failure during logout — log and fall through so local state
      // is always cleared, preventing a phantom "logged in" session.
      console.error("[auth] Logout request failed (network):", err);
    } finally {
      set({ user: null, isAuthenticated: false });
    }
  },

  refreshSession: async () => {
    const res = await fetch("/api/v2/auth/refresh", {
      method: "POST",
      headers: { "X-CSRF-Token": getCsrfToken() },
      credentials: "include",
    });
    if (!res.ok) {
      set({ user: null, isAuthenticated: false });
    }
  },

  fetchMe: async () => {
    set({ isLoading: true });
    const res = await fetch("/api/v2/auth/me", { credentials: "include" });
    if (res.ok) {
      // v2 returns the raw UserOut object (no envelope).
      const data = await res.json();
      set({
        user: {
          userId: data.id,
          email: data.email ?? "",
          displayName: data.full_name ?? "",
          role: data.role,
          tenantId: "",
          permissions: data.permissions ?? [],
        },
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
}));
